'use client';

import React from 'react';

import { useGuestGate } from '@/hooks/useGuestGate';

interface GuestClickGuardProps {
  children: React.ReactNode;
}

// Native/ARIA interactive elements whose clicks should open the login modal.
const INTERACTIVE_SELECTOR = 'a, button, [role="button"], [role="link"], [role="tab"]';

/**
 * True when the click landed on something the user meant to interact with — a
 * native control/role, or any clickable-styled ancestor (`cursor: pointer`). The
 * latter catches the many card UIs that navigate via a `<div onClick>` rather than
 * an anchor/button, while leaving plain text / whitespace clicks alone.
 */
const isInteractiveClick = (target: HTMLElement, boundary: HTMLElement): boolean => {
  if (target.closest(INTERACTIVE_SELECTOR)) return true;
  let node: HTMLElement | null = target;
  while (node && node !== boundary) {
    if (window.getComputedStyle(node).cursor === 'pointer') return true;
    node = node.parentElement;
  }
  return false;
};

/**
 * Wraps a subtree and, for signed-out users, intercepts clicks on interactive
 * elements — opening the login modal instead of letting the navigation/action run.
 *
 * Uses a capture-phase handler on a `display: contents` wrapper (so it adds no
 * layout box): the guard fires before the target's own `onClick`, and
 * `stopPropagation()` prevents that handler (e.g. a Next.js `<Link>` navigation)
 * from executing.
 *
 * Escape hatch: add `data-guest-allow` to any element that should keep working for
 * guests inside a guarded region.
 */
export const GuestClickGuard = ({ children }: GuestClickGuardProps) => {
  const { isGuest, guardGuestAction } = useGuestGate();

  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isGuest) return;
    const target = e.target as HTMLElement | null;
    if (!target || target.closest('[data-guest-allow]')) return;
    if (!isInteractiveClick(target, e.currentTarget)) return;
    guardGuestAction(e);
  };

  return (
    <div style={{ display: 'contents' }} onClickCapture={handleClickCapture}>
      {children}
    </div>
  );
};

export default GuestClickGuard;
